/**
 * tent-addon-selector.js
 * Adds the selected tier's add-ons together with the main tent in a single
 * /cart/add request (by patching window.fetch), then refreshes/opens the
 * Krank cart drawer.
 *
 * Integration with the Rocky Mountain Tent upsell flow:
 * When the upsell takes over a tent Add to Cart it sets
 * window.__UO_UPSELL_HANDLING_ADD = true. While that flag is set we still
 * append the add-ons, but we do NOT open the cart drawer, because the
 * upsell modal owns the drawer and opens it at the end of its flow.
 */

(function () {
  'use strict';

  class TentAddonSelector {
    constructor(form) {
      this.form         = form;
      this.sectionId    = form.closest('[data-section]')?.dataset.section || '';
      this.addonFields  = form.querySelector('.tas-addon-fields');
      this.totalEl      = document.getElementById('TAS-Total-' + this.sectionId);
      this.cards        = Array.from(form.querySelectorAll('.tas-card'));
      this.activeTier   = 0;
      this.crossbarId   = this.getMatchedVariantId('crossbar');   // ← matched
      this.insulationId = this.getMatchedVariantId('insulation'); // ← matched

      this.tierPriceStrings = this.cacheTierPrices();
      this.bindEvents();
      this.applyTier(0, false);
      this.interceptAddToCart();
    }

    // ─── Generic variant matcher — crossbar ya insulation dono k liye ───────
    getMatchedVariantId(type) {
      const addonFields = this.addonFields;
      if (!addonFields) return '';

      const productTitle = (addonFields.dataset.productTitle || '').toLowerCase();
      const variantsRaw  = addonFields.dataset[type + 'Variants'] || '';
      const fallback     = addonFields.dataset[type + 'Id'] || '';

      console.log(`[TAS][${type}] productTitle:`, productTitle);
      console.log(`[TAS][${type}] variantsRaw:`, variantsRaw);

      if (!variantsRaw) {
        console.warn(`[TAS][${type}] No variants data, using fallback`);
        return fallback;
      }

      const keywords = ['wolverine', 'grizzly', 'timberwolf', 'owl'];
      const pairs    = variantsRaw.split('|');

      for (const pair of pairs) {
        const colonIndex   = pair.indexOf(':');
        const id           = pair.substring(0, colonIndex).trim();
        const variantTitle = pair.substring(colonIndex + 1).trim().toLowerCase();

        console.log(`[TAS][${type}] checking:`, variantTitle, '| id:', id);

        for (const kw of keywords) {
          if (productTitle.includes(kw) && variantTitle.includes(kw)) {
            console.log(`[TAS][${type}] matched! keyword: ${kw} → id: ${id}`);
            return id;
          }
        }
      }

      console.warn(`[TAS][${type}] No match found, using fallback`);
      return fallback;
    }
    // ────────────────────────────────────────────────────────────────────────

    getCartSectionId() {
      const cartDrawer = document.querySelector('cart-drawer[data-section]');
      return cartDrawer?.dataset.section || null;
    }

    cacheTierPrices() {
      return this.cards.reduce((acc, card) => {
        const idx     = parseInt(card.dataset.tier, 10);
        const priceEl = card.querySelector('.tas-card__price');
        acc[idx]      = priceEl ? priceEl.textContent.trim() : '';
        return acc;
      }, {});
    }

    bindEvents() {
      this.cards.forEach(card => {
        card.addEventListener('click', () =>
          this.applyTier(parseInt(card.dataset.tier, 10))
        );
        card.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.applyTier(parseInt(card.dataset.tier, 10));
          }
        });
      });
    }

    applyTier(tier, animate = true) {
      this.activeTier = tier;
      this.cards.forEach(card => {
        const isActive = parseInt(card.dataset.tier, 10) === tier;
        card.classList.toggle('tas-card--active', isActive);
        card.setAttribute('aria-checked', isActive ? 'true' : 'false');
        if (animate && isActive) {
          card.classList.add('tas-card--just-selected');
          setTimeout(() => card.classList.remove('tas-card--just-selected'), 300);
        }
      });
      if (this.totalEl && this.tierPriceStrings[tier]) {
        this.totalEl.textContent = this.tierPriceStrings[tier];
      }
    }

    interceptAddToCart() {
      const self       = this;
      const origFetch  = window.fetch;
      let   processing = false;

      const bundleForm  = document.getElementById('Bundle-Sale-' + this.sectionId);
      const mainVariant = bundleForm?.dataset.variantId || '';

      window.fetch = async function(input, init, ...rest) {
        const url    = typeof input === 'string' ? input : (input?.url || '');
        const method = (init?.method || 'GET').toUpperCase();

        if (!processing && url.includes('/cart/add') && method === 'POST' && mainVariant) {
          const bodyStr = self.bodyToString(init?.body);

          if (bodyStr.includes(mainVariant) && self.activeTier > 0) {
            processing = true;

            const extraItems    = self.getAddonItems();
            const cartSectionId = self.getCartSectionId();

            if (extraItems.length > 0) {
              try {
                const parsed        = JSON.parse(bodyStr);
                const existingItems = parsed.items || [{ id: parsed.id, quantity: parsed.quantity || 1 }];
                const allItems      = [...existingItems, ...extraItems];

                const combinedBody = JSON.stringify({
                  items:        allItems,
                  sections:     cartSectionId ? [cartSectionId] : [],
                  sections_url: window.location.pathname
                });

                const newInit  = { ...init, body: combinedBody };
                const response = await origFetch.call(this, input, newInit, ...rest);
                processing     = false;

                self.refreshCartDrawer(cartSectionId);
                return response;

              } catch(e) {
                const response = await origFetch.call(this, input, init, ...rest);
                try {
                  await self.addAddonsSeparately();
                } catch(err) {
                  console.warn('[TAS] addon add failed:', err);
                }
                processing = false;
                self.refreshCartDrawer(cartSectionId);
                return response;
              }
            }

            processing = false;
          }
        }

        return origFetch.call(this, input, init, ...rest);
      };
    }

    getAddonItems() {
      const qty   = this.getMainQty();
      const items = [];
      if (this.activeTier >= 1 && this.crossbarId) {
        items.push({ id: parseInt(this.crossbarId, 10), quantity: qty });
      }
      if (this.activeTier >= 2 && this.insulationId) {
        items.push({ id: parseInt(this.insulationId, 10), quantity: qty });
      }
      return items;
    }

    async addAddonsSeparately() {
      const items = this.getAddonItems();
      if (!items.length) return;

      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ items })
      });

      if (!res.ok) throw new Error('Add-on cart add failed');
      return res.json();
    }

    refreshCartDrawer(cartSectionId) {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cartData => {
          const count = cartData.item_count || 0;
          document.querySelectorAll('.cart-count').forEach(el => {
            el.textContent = count;
          });
        })
        .catch(() => {});

      if (!cartSectionId) {
        this.openDrawerNatively();
        return;
      }

      fetch(`/?sections=${cartSectionId}`)
        .then(r => r.json())
        .then(data => {
          const html = data[cartSectionId];
          if (!html) {
            this.openDrawerNatively();
            return;
          }

          const parser = new DOMParser();
          const doc    = parser.parseFromString(html, 'text/html');

          const freshTbody    = doc.querySelector('.cart-items-list tbody');
          const existingTbody = document.querySelector('.cart-items-list tbody');
          if (freshTbody && existingTbody) {
            existingTbody.innerHTML = freshTbody.innerHTML;
            existingTbody.querySelectorAll('img[data-srcset]').forEach(img => {
              const dataSrcset = img.getAttribute('data-srcset');
              if (dataSrcset) {
                img.setAttribute('srcset', dataSrcset);
                const firstUrl = dataSrcset.split(',')[0].trim().split(' ')[0];
                if (firstUrl) img.src = firstUrl;
                img.removeAttribute('loading');
              }
            });
          }

          const freshFooter    = doc.querySelector('.cart-details-footer');
          const existingFooter = document.querySelector('.cart-details-footer');
          if (freshFooter && existingFooter) {
            existingFooter.innerHTML = freshFooter.innerHTML;
          }

          this.openDrawerNatively();
        })
        .catch(e => {
          console.warn('[TAS] Refresh failed:', e);
          this.openDrawerNatively();
        });
    }

    openDrawerNatively() {
      if (window.__UO_UPSELL_HANDLING_ADD) return;

      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else {
        cartDrawer.setAttribute('open', '');
        const inner = cartDrawer.querySelector('[data-trap]');
        if (inner) inner.focus();
      }

      setTimeout(() => {
        const scrollTop = Math.abs(parseInt(document.body.style.top || '0', 10));
        document.body.removeAttribute('scroll-y-off');
        document.body.style.top = '';
        if (scrollTop > 0) {
          window.scrollTo(0, scrollTop);
        }
      }, 50);
    }

    bodyToString(body) {
      if (!body) return '';
      if (typeof body === 'string') return body;
      if (body instanceof FormData)        return new URLSearchParams([...body]).toString();
      if (body instanceof URLSearchParams) return body.toString();
      return '';
    }

    getMainQty() {
      const sectionEl = this.form.closest('[data-section]');
      const input     = sectionEl?.querySelector('input[name="quantity"], .quantity__input');
      return parseInt(input?.value || '1', 10);
    }
  }

  // Bootstrap
  function init() {
    document.querySelectorAll('.tent-addon-selector form').forEach(form => {
      if (!form._tasInit) {
        form._tasInit = true;
        new TentAddonSelector(form);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();